import type { EntitySearchCountsByEntityType, EntitySearchResponse } from "@/types/search/EntitySearchResponse";
import type { IndexableDocument } from "@/types/search/IndexableDocument";
import { getDisplayFileName, getDownloadFileName, getFileExtension, isPdfUpload } from "@/helpers/files";

/**
 * LSP-16659: the Session and Project media grids hold document rows (`entityType === "document"`,
 * one per `Files` row) alongside photos and videos. These helpers are the one place that decides how a
 * document row is named, classified, and kept apart from the photo/video-only surfaces (the lightbox,
 * checklists, sheets, day-view date drag).
 */

function identityOf(row: IndexableDocument): string {
	return row.searchResultId ?? row.id;
}

export function isTimelineDocument(row: Pick<IndexableDocument, "entityType"> | null | undefined): boolean {
	return row?.entityType === "document";
}

/** Rows MediaLightBox can show: everything that is not a document. */
export function isLightboxMediaRow(row: Pick<IndexableDocument, "entityType"> | null | undefined): boolean {
	return !!row && !isTimelineDocument(row);
}

/** The tile header and PDF viewer title: the real uploaded name, else the created date (never the S3 key). */
export function getTimelineDocumentName(row: IndexableDocument): string {
	return getDisplayFileName(row);
}

/**
 * PDF vs any-other-file. `isPdfUpload` settles it from the hydrated `mimeType`, an explicit extension or
 * the uploaded name; the signed URL's path is the last resort for an unnamed row whose mime type is absent.
 */
export function isPdfTimelineDocument(row: IndexableDocument): boolean {
	if (isPdfUpload(row)) return true;
	return getFileExtension({ uploadResult: { key: row?.mediaUrl ?? "" } }) === "pdf";
}

/** A path-safe download name that keeps the file's real extension. */
export function getTimelineDocumentDownloadName(row: IndexableDocument): string {
	return getDownloadFileName(row, row?.mediaUrl);
}

export interface LightboxMediaWriteResult {
	/** The merged list to commit, or null when the write changes nothing (skip the reactive replacement). */
	documents: IndexableDocument[] | null;
	/** Emitted rows that were refused because they are documents — the lightbox must never own one. */
	refusedDocumentRows: IndexableDocument[];
}

/**
 * LSP-16659: write-back half of the lightbox adapter. MediaLightBox emits WHOLE replacement arrays of the
 * media it was handed (annotation placeholders inserted after their parent, removals, playback-heal field
 * merges). Those arrays contain no documents, so assigning one straight to `mediaResponse.documents`
 * would delete every document tile.
 *
 * The merge keeps the emitted media order EXACTLY (including a placeholder the lightbox put right after
 * its parent) and re-seats each document immediately before the media row that followed it in the current
 * list. If that row is gone, the next surviving one is used; with none left, the document goes to the end.
 * Anchoring to the FOLLOWING row, not the preceding one, is what keeps an inserted placeholder adjacent
 * to its parent instead of on the far side of a document.
 */
export function mergeLightboxMediaWrite(current: IndexableDocument[], emitted: IndexableDocument[]): LightboxMediaWriteResult {
	const refusedDocumentRows = emitted.filter((row) => isTimelineDocument(row));
	const emittedMedia = refusedDocumentRows.length ? emitted.filter((row) => !isTimelineDocument(row)) : emitted;
	const emittedIdentities = new Set(emittedMedia.map(identityOf));

	const documentsBefore = new Map<string, IndexableDocument[]>();
	const trailingDocuments: IndexableDocument[] = [];
	let waitingDocuments: IndexableDocument[] = [];
	for (const row of current) {
		if (isTimelineDocument(row)) {
			waitingDocuments.push(row);
			continue;
		}
		if (waitingDocuments.length && emittedIdentities.has(identityOf(row))) {
			const anchor = identityOf(row);
			documentsBefore.set(anchor, [...(documentsBefore.get(anchor) ?? []), ...waitingDocuments]);
			waitingDocuments = [];
		}
	}
	trailingDocuments.push(...waitingDocuments);

	const merged: IndexableDocument[] = [];
	for (const row of emittedMedia) {
		const anchored = documentsBefore.get(identityOf(row));
		if (anchored) {
			merged.push(...anchored);
			documentsBefore.delete(identityOf(row));
		}
		merged.push(row);
	}
	merged.push(...trailingDocuments);

	const unchanged = merged.length === current.length && merged.every((row, index) => row === current[index]);
	return { documents: unchanged ? null : merged, refusedDocumentRows };
}

/**
 * LSP-16659: does the page hold photos or videos (as opposed to only files)? Uses the server's per-type
 * totals when the response carries them, so a grid of 100 .docx files with 75 loaded is still "no media".
 * A loaded photo/video row always counts: a delta merge can add one before the totals are refreshed.
 * Without per-type totals (a response that did not ask for documents) `count` is photos + videos only.
 */
export function hasPhotoOrVideoMedia(response: EntitySearchResponse | null | undefined): boolean {
	if (!response) return false;
	if ((response.documents ?? []).some((row) => isLightboxMediaRow(row))) return true;
	const counts = response.countsByEntityType;
	if (counts) return (counts.snapshot ?? 0) + (counts.recording ?? 0) > 0;
	return (response.count ?? 0) > 0;
}

/**
 * LSP-23170: the total behind the mobile lightbox's "N of M" — the grid's photos + videos across EVERY page,
 * not just the loaded ones. Only the per-type totals give that: they come back only when the request asked
 * for documents, which also forces the API's exact count. Without them `count` either includes documents
 * (which the lightbox never shows) or is the inexact "loaded + 1" probe of a photo/video-only filter, so the
 * answer is null and the lightbox hides the counter rather than show a total that is too low.
 */
export function getLightboxMediaTotal(response: EntitySearchResponse | null | undefined): number | null {
	const counts = response?.countsByEntityType;
	if (!counts || response?.countCapped === true) return null;
	const total = (counts.snapshot ?? 0) + (counts.recording ?? 0);
	return Number.isFinite(total) ? total : null;
}

/**
 * Per-type totals after a DELTA merge. The delta response's own totals are windowed (only rows newer than
 * the start date), so — exactly like `count` — the page's totals are adjusted by the net-new rows per type.
 */
export function adjustCountsByEntityType(
	counts: EntitySearchCountsByEntityType | undefined,
	previousRows: IndexableDocument[],
	mergedRows: IndexableDocument[]
): EntitySearchCountsByEntityType | undefined {
	if (!counts) return counts;
	const tally = (rows: IndexableDocument[], entityType: keyof EntitySearchCountsByEntityType) =>
		rows.filter((row) => row.entityType === entityType).length;
	const adjusted = { ...counts };
	for (const entityType of ["snapshot", "recording", "document"] as const) {
		adjusted[entityType] = Math.max(0, (counts[entityType] ?? 0) + tally(mergedRows, entityType) - tally(previousRows, entityType));
	}
	return adjusted;
}

/**
 * In-place removal: keep the per-type totals in step with `count` when a row is spliced out of the grid
 * (lightbox delete, bulk delete). Without it, deleting the last photo beside only non-PDF files left the
 * stale snapshot total keeping Sparky enabled — and the checklist media count overstated — until the next
 * reload. Never below 0; absent totals stay absent (the page then falls back to `count`), and a type the
 * totals do not track is ignored.
 */
export function decrementCountsByEntityType(
	response: EntitySearchResponse | null | undefined,
	entityType: string | null | undefined
): void {
	const counts = response?.countsByEntityType;
	if (!counts || (entityType !== "snapshot" && entityType !== "recording" && entityType !== "document")) return;
	counts[entityType] = Math.max(0, (counts[entityType] ?? 0) - 1);
}
